import React from 'react';
import { BuildingIcon, HandshakeIcon, UsersIcon, UsersRoundIcon } from 'lucide-react';
import type { NetworkCampus } from '../../types/network';
interface TrustStripProps {
  campuses: NetworkCampus[];
}

/**
 * What Campuzon is built for, stated as roles rather than as counts.
 *
 * No student, provider or community total is published here. Inventing one
 * would be the easiest lie on the page, and the strip works without it: the
 * four audiences are the point, and the only numbers shown come from the
 * campus register, which is real configuration.
 */
const AUDIENCES = [{
  icon: UsersIcon,
  label: 'Students',
  note: 'Connected'
}, {
  icon: BuildingIcon,
  label: 'Campuses',
  note: 'Onboarded'
}, {
  icon: HandshakeIcon,
  label: 'Providers',
  note: 'Verified'
}, {
  icon: UsersRoundIcon,
  label: 'Communities',
  note: 'Active'
}];
export function TrustStrip({
  campuses
}: TrustStripProps) {
  const operating = campuses.filter(campus => campus.published && campus.active);
  return <section aria-label="Who Campuzon is built for" className="border-y border-line bg-canvas" data-id="element-2470" data-mp="32uathewbpifg">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:items-center lg:gap-10" data-id="element-2471" data-mp="37ugp165prp2t">
        <p className="shrink-0 text-body-sm font-semibold leading-6 text-ink" data-id="element-2472" data-mp="2t3zorq4tf57i">
          Built for
          <br className="hidden lg:block" data-id="element-2473" data-mp="2y32iiefchp06" /> the modern campus
        </p>

        <ul className="grid grow grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4 lg:border-l lg:border-line lg:pl-10" data-id="element-2474" data-mp="2k4mfoyklsinb">
          {AUDIENCES.map(item => {
          const Icon = item.icon;
          return <li key={item.label} className="flex items-center gap-2.5" data-id="element-2475" data-mp="2a6ygnwfysozc">
                <Icon aria-hidden className="h-5 w-5 shrink-0 text-brand-700" data-id="element-2476" data-mp="2f59sudtqfbkq" />
                <span className="min-w-0" data-id="element-2477" data-mp="2f7chouj5eynv">
                  <span className="block text-body-sm font-semibold text-ink" data-id="element-2478" data-mp="8ek0p2wzdulk">{item.label}</span>
                  <span className="block text-caption text-ink-muted" data-id="element-2479" data-mp="de51livpod0x">{item.note}</span>
                </span>
              </li>;
        })}
        </ul>

        <div className="shrink-0 lg:border-l lg:border-line lg:pl-10" data-id="element-2480" data-mp="bud77i2tqwph">
          <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2481" data-mp="6wbm6rlu8nbd">
            Campuses in the register
          </p>
          <ul className="mt-2 flex flex-wrap gap-2" data-id="element-2482" data-mp="3u2m4iqwpuqpu">
            {campuses.filter(campus => campus.published).map(campus => <li key={campus.id} className="rounded-full border border-line bg-surface px-3 py-1 text-caption font-medium text-ink" data-id="element-2483" data-mp="upbncm6yp9qa">
                  {campus.institution.replace('Campuzon ', '')}
                </li>)}
          </ul>
          <p className="cz-num mt-2 text-caption text-ink-subtle" data-id="element-2484" data-mp="gtq12ms4541k">
            {operating.length} operating. Campuses appear here only once configured — never as a
            claimed partnership.
          </p>
        </div>
      </div>
    </section>;
}